import { useLayoutEffect, useRef, useState } from 'react'

import { timelineStemSegmentBackground } from '../../lib/timelineStem'
import { useTimelineSpineRegistry } from './TimelineSpineContext'

/** 节点圆心处略重叠，避免亚像素缝隙 */
const CONNECT_OVERLAP_PX = 1

type MeasuredSegment = {
  top: number
  height: number
  background: string
  rounded: boolean
}

function segmentsEqual(a: MeasuredSegment[], b: MeasuredSegment[]) {
  if (a.length !== b.length) return false
  return a.every(
    (seg, i) =>
      seg.top === b[i]?.top &&
      seg.height === b[i]?.height &&
      seg.background === b[i]?.background &&
      seg.rounded === b[i]?.rounded,
  )
}

function dotCenterY(el: HTMLElement, hostTop: number): number {
  const rect = el.getBoundingClientRect()
  return rect.top + rect.height / 2 - hostTop
}

export function TimelineListSpine({ angular }: { angular: boolean }) {
  const { roundIds, segmentStops, getDot, getContent, subscribeLayout } =
    useTimelineSpineRegistry()
  const hostRef = useRef<HTMLDivElement>(null)
  const [segments, setSegments] = useState<MeasuredSegment[]>([])

  useLayoutEffect(() => {
    const host = hostRef.current
    if (!host || roundIds.length === 0) {
      setSegments((prev) => (prev.length === 0 ? prev : []))
      return
    }

    const measure = () => {
      const hostRect = host.getBoundingClientRect()
      const hostTop = hostRect.top
      const next: MeasuredSegment[] = []

      for (let i = 0; i < roundIds.length; i++) {
        const dot = getDot(roundIds[i]!)
        if (!dot) continue

        const top = dotCenterY(dot, hostTop) - CONNECT_OVERLAP_PX
        const nextDot = i < roundIds.length - 1 ? getDot(roundIds[i + 1]!) : undefined

        let bottom: number
        if (nextDot) {
          bottom = dotCenterY(nextDot, hostTop) + CONNECT_OVERLAP_PX
        } else {
          const content = getContent(roundIds[i]!)
          bottom = content
            ? content.getBoundingClientRect().bottom - hostTop + CONNECT_OVERLAP_PX
            : dot.getBoundingClientRect().bottom - hostTop + CONNECT_OVERLAP_PX
        }

        const height = Math.max(0, bottom - top)
        if (height < 1) continue

        const stop = segmentStops[i]
        const background =
          nextDot && stop
            ? timelineStemSegmentBackground(stop.from, stop.to)
            : stop
              ? stop.from
              : 'color-mix(in srgb, var(--line) 72%, transparent)'

        next.push({
          top,
          height,
          background,
          rounded: !angular,
        })
      }

      setSegments((prev) => (segmentsEqual(prev, next) ? prev : next))
    }

    measure()

    const ro = new ResizeObserver(measure)
    ro.observe(host)

    const observed = new WeakSet<Element>()
    const observeTargets = () => {
      for (const id of roundIds) {
        for (const el of [getDot(id), getContent(id)]) {
          if (!el || observed.has(el)) continue
          observed.add(el)
          ro.observe(el)
        }
      }
    }
    observeTargets()

    const unsubscribe = subscribeLayout(() => {
      observeTargets()
      measure()
    })

    return () => {
      unsubscribe()
      ro.disconnect()
    }
  }, [roundIds, segmentStops, getDot, getContent, subscribeLayout, angular])

  return (
    <div
      ref={hostRef}
      className="pointer-events-none relative col-start-2 row-start-1 row-end-[-1] z-0 min-h-0 justify-self-center self-stretch"
      aria-hidden
    >
      {segments.map((seg, idx) => (
        <div
          key={`${idx}-${seg.top}-${seg.height}`}
          className={[
            'absolute left-1/2 w-1 -translate-x-1/2',
            seg.rounded ? 'rounded-full' : 'rounded-none',
          ].join(' ')}
          style={{
            top: seg.top,
            height: seg.height,
            background: seg.background,
          }}
        />
      ))}
    </div>
  )
}
